import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';

const MasterCardManagement = ({ isViewer }) => {
  const [masterCards, setMasterCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMasterCard, setEditingMasterCard] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    lastFourDigits: '',
    glAccount: '',
    isActive: true
  });

  const fetchMasterCards = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/master-cards');
      setMasterCards(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching master cards:', error);
      setError('Failed to fetch master cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterCards();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.lastFourDigits || formData.lastFourDigits.length !== 4 || !/^\d{4}$/.test(formData.lastFourDigits)) {
      setError('Last four digits must be exactly 4 digits');
      return;
    }

    if (!formData.glAccount.trim()) {
      setError('GL Account is required');
      return;
    }

    try {
      if (editingMasterCard) {
        await axios.put(`/api/master-cards/${editingMasterCard.id}`, formData);
        setSuccess('Master card updated successfully');
      } else {
        await axios.post('/api/master-cards', formData);
        setSuccess('Master card created successfully');
      }

      setShowModal(false);
      setEditingMasterCard(null);
      resetForm();
      fetchMasterCards();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving master card:', error);
      setError(error.response?.data?.error || 'Failed to save master card');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEdit = (masterCard) => {
    setEditingMasterCard(masterCard);
    setFormData({
      name: masterCard.name,
      lastFourDigits: masterCard.lastFourDigits,
      glAccount: masterCard.glAccount,
      isActive: masterCard.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this master card?')) {
      return;
    }

    try {
      await axios.delete(`/api/master-cards/${id}`);
      setSuccess('Master card deleted successfully');
      fetchMasterCards();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting master card:', error);
      setError(error.response?.data?.error || 'Failed to delete master card');
      setTimeout(() => setError(''), 5000);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      lastFourDigits: '',
      glAccount: '',
      isActive: true
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMasterCard(null);
    resetForm();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Master Card Management</h2>
        {!isViewer && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Master Card
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last 4 Digits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GL Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linked Cards
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {masterCards.map((masterCard) => (
                <tr key={masterCard.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{masterCard.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{masterCard.lastFourDigits}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{masterCard.glAccount}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs ${
                      masterCard.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {masterCard.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {masterCard.creditCards?.length || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!isViewer && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(masterCard)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(masterCard.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">
              {editingMasterCard ? 'Edit Master Card' : 'Add Master Card'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last 4 Digits *
                  </label>
                  <input
                    type="text"
                    value={formData.lastFourDigits}
                    onChange={(e) => setFormData({ ...formData, lastFourDigits: e.target.value })}
                    maxLength="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GL Account *
                  </label>
                  <input
                    type="text"
                    value={formData.glAccount}
                    onChange={(e) => setFormData({ ...formData, glAccount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  {editingMasterCard ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterCardManagement;
